/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { marketingClickInfoInvoker, triggerMarketingEvent } from '@/data/Content/_Marketing';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { PRODUCT_DATA_KEY, productFetcher } from '@/data/Content/_Product';
import { getLocalization, useLocalization } from '@/data/Localization';
import { getContractIdParamFromContext, getSettings, useSettings } from '@/data/Settings';
import { getUser, useUser } from '@/data/User';
import { getServerCacheScope } from '@/data/cache/getServerCacheScope';
import { SORT_OPTIONS, topCategoryIds } from '@/data/constants/catalog';
import { ID } from '@/data/types/Basic';
import { ContentProps } from '@/data/types/ContentProps';
import {
	ProductFacetEntry,
	ProductQueryResponse,
	ProductType,
	ResponseProductType,
} from '@/data/types/Product';
import { constructRequestParamsWithPreviewToken } from '@/data/utils/constructRequestParams';
import { extractContentsArray } from '@/data/utils/extractContentsArray';
import { extractFacetsArray } from '@/data/utils/extractFacetsArray';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { getIdFromPath } from '@/data/utils/getIdFromPath';
import { getProductListFacetFilters } from '@/data/utils/getProductListFacetFilters';
import { getProductListQueryParameters } from '@/data/utils/getProductListQueryParameters';
import { getServerSideCommon } from '@/data/utils/getServerSideCommon';
import { expand, shrink } from '@/data/utils/keyUtil';
import { laggyMiddleWare } from '@/data/utils/laggyMiddleWare';
import { mapFacetEntryData } from '@/data/utils/mapFacetData';
import { mapProductData } from '@/data/utils/mapProductData';
import { seoObfuscatorMiddleWare } from '@/data/utils/swr/seoObfuscatorMiddleWare';
import { SelectChangeEvent } from '@mui/material';
import { Translation } from 'integration/generated/translations';
import { isEmpty, union, uniq } from 'lodash';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import useSWR, { unstable_serialize as unstableSerialize } from 'swr';
import { getBreadcrumbTrail } from './BreadcrumbTrail-Server';
import { Breadcrumb } from '../types/Breadcrumb';
import { useBreadcrumbTrail } from './BreadcrumbTrail';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { getCategories, useCategories } from './Categories';
import { getFlexFlowStoreFeature } from '@/data/Content/FlexFlowStoreFeature-Server';
import { EMS_STORE_FEATURE } from '@/data/constants/flexFlowStoreFeature';
import { ParsedUrlQuery } from 'querystring';
import { getHrefAsQueryString } from '../utils/getQueryAsString';
import { getPageNumberForPaginationItem } from '../utils/getPageNumberForPaginationItem';
import { collectionPageData as defaultCollectionPageData } from '@/data/constants/structuredData';

const DATA_KEY = PRODUCT_DATA_KEY; 

const fetcher = productFetcher;

const dataMap = (data?: ProductQueryResponse, query?:ParsedUrlQuery) => {
	const products = (extractContentsArray(data) as ResponseProductType[]).map(mapProductData);
	const metaData = data?.metaData;
	let categoryCanonicalPath = data?.x_canonicals?.at(0)??null;
	if(categoryCanonicalPath && query?.offset){
		categoryCanonicalPath = `${categoryCanonicalPath}?offset=${query.offset}`;
	}
	return { products, metaData, categoryCanonicalPath };
};

const facetEntryDataMap = (data?: ProductQueryResponse) =>
	extractFacetsArray(data).map(mapFacetEntryData).flat(1);

const fetchLocalization = async ({ cache, context }: ContentProps) =>
	await Promise.all([
		getLocalization(cache, context.locale || 'en-US', 'ProductGrid'),
		getLocalization(cache, context.locale || 'en-US', 'Category'),
		getLocalization(cache, context.locale || 'en-US', 'ProductFilter'),
		getLocalization(cache, context.locale || 'en-US', 'compare'),
		getLocalization(cache, context.locale || 'en-US', 'PriceDisplay'),
		getLocalization(cache, context.locale || 'en-US', 'CommerceEnvironment'),
		getLocalization(cache, context.locale || 'en-US', 'Common'),
	]);

export const getCatalogEntryList = async ({
	cache,
	id,
	context,
}: ContentProps): Promise<
	{
		key: string;
		value: ProductQueryResponse;
	}[]
> => {
	const settings = await getSettings(cache, context);
	const user = await getUser(cache, context);
	const {
		storeId,
		langId,
		defaultCurrency: currency,
		defaultCatalogId: catalogId,
	} = getServerSideCommon(settings, context);
	await Promise.all([
		fetchLocalization({ cache, id, context }),
		getFlexFlowStoreFeature({ cache, id: EMS_STORE_FEATURE.GUEST_SHOPPING, context }),
	]);
	await fetchLocalization({ cache, id, context });
	const breadcrumb = await getBreadcrumbTrail({cache,id,context});
	const breadcrumbArr:Breadcrumb[] = breadcrumb?.rc??[];
	let parentCategoryId = breadcrumbArr?.at(-2)?.value;
	if(!parentCategoryId){
		const tmpCategoryId = breadcrumbArr?.at(-1)?.value;
		// Check if top category, do not update otherwise. 
		if(tmpCategoryId)
		parentCategoryId = topCategoryIds.find((tmpId => tmpId === tmpCategoryId));
	} 
	const filteredParams = getProductListQueryParameters(context.query);
	const { Search } = await getLocalization(cache, context.locale || 'nl-NL', 'Routes');
	const path = getIdFromPath(context.query.path, settings.storeToken);
	const { profileName, categoryId } =
		path === (Search as Translation)?.route
			? { profileName: 'Intertoys_V2_findProductsBySearchTerm', categoryId: '' }
			: { profileName: 'Intertoys_V2_findProductsByCategory', categoryId: String(id) };
	const props = {
		...filteredParams,
		storeId,
		categoryId,
		catalogId,
		profileName,
		langId,
		currency,
		parentCategoryId,
		...getContractIdParamFromContext(user?.context),
	};
	const cacheScope = getServerCacheScope(context, user.context);
	const key = unstableSerialize([shrink(props), DATA_KEY]);
	const params = constructRequestParamsWithPreviewToken({ context });
	const value = cache.get(key, cacheScope) || fetcher(false, context)(props, params);
	cache.set(key, value, cacheScope);
	
	if(id?.toString())await getCategories({cache,id,context});//Needed to get get the category details

	return [
		{
			key,
			value: await value,
		},
	];
};

export const useCatalogEntryList = (id: ID) => {
	const { settings } = useSettings();
	const { user } = useUser();
	const router = useNextRouter();
	const {
		storeId,
		langId,
		defaultCatalogId: catalogId,
		defaultCurrency: currency,
	} = getClientSideCommon(settings, router);
	const { query } = router;
	const params = useExtraRequestParameters();
	const filteredParams = useMemo(() => getProductListQueryParameters(query), [query]);
	const facetFilters = useMemo(() => getProductListFacetFilters(filteredParams), [filteredParams]);
	const SearchLocalization = useLocalization('Routes').Search;
	const path = getIdFromPath(query.path, settings.storeToken);
	const basePath = useMemo(()=>settings.userData.SiteLink_DomainName,[settings]);
	const {breadcrumb} = useBreadcrumbTrail();
	let parentCategoryId = breadcrumb?.at(-2)?.value;
	if(!parentCategoryId){
		const tmpCategoryId = breadcrumb?.at(-1)?.value;
		// Check if top category, do not update otherwise. 
		if(tmpCategoryId)
		parentCategoryId = topCategoryIds.find((tmpId => tmpId === tmpCategoryId));
	} 
	const { profileName, categoryId } =
		path === SearchLocalization.route.t()
			? { profileName: 'Intertoys_V2_findProductsBySearchTerm', categoryId: '' }
			: { profileName: id!==EMPTY_STRING?'Intertoys_V2_findProductsByCategory':undefined, categoryId: String(id) };
	const { data, error } = useSWR(
		storeId && profileName
			? [
					shrink({
						...filteredParams,
						storeId,
						categoryId,
						catalogId,
						profileName,
						langId,
						currency,
						parentCategoryId,
						...getContractIdParamFromContext(user?.context),
					}),
					DATA_KEY,
			  ]
			: null,
		async ([props]) => fetcher(true)(expand(props), params),
		{ use: [seoObfuscatorMiddleWare, laggyMiddleWare] }
	);
	const { products, metaData , categoryCanonicalPath} = useMemo(() => dataMap(data, query), [data]);
	const [facetEntries, setFacetEntries] = useState<ProductFacetEntry[]>(() =>
		facetEntryDataMap(data)
	);

	const total = useMemo(() => data?.total, [data]);

	const {catGrpDetails} = useCategories(id?.toString()?[id?.toString()]:[]);
	
	let selectedFacetDup = useMemo(
		() => (query.facet ? (Array.isArray(query.facet) ? query.facet : [query.facet]) : []),
		[query.facet]
	);
	const selectedFacet = uniq(selectedFacetDup);

	const onSortOptionChange = useCallback(
		(event: SelectChangeEvent<string>, _child: ReactNode) => {
			const orderBy = event.target.value;
			const { orderBy: _orderBy, limit, ...newQuery } = filteredParams; // use query instead to respect other params?
			const pathname = router.asPath.split('?').at(0);

			if (orderBy !== '0') {
				Object.assign(newQuery, { orderBy });
			}
			router.push({ pathname, query: newQuery }, undefined, {
				shallow: true,
			});
		},
		[filteredParams, router]
	);

	const onPageChange = useCallback(
		(page: number) => {
			const { limit, offset: _offset, ...newQuery } = filteredParams; // use query instead to respect other params?
			const offset = (page - 1) * limit;
			const pathname = router.asPath.split('?').at(0);
			Object.assign(newQuery, { offset });
			router.push({ pathname, query: newQuery }, undefined, {
				shallow: true,
			});
		},
		[filteredParams, router]
	);

	const getPageLink = useCallback(
		(page: number) => {
			const { limit, offset: _offset, ...newQuery } = filteredParams; // use query instead to respect other params?
			const offset = (page - 1) * limit;
			const pathname = basePath+router.asPath.split('?').at(0);
			offset && Object.assign(newQuery, { offset });
			return { pathname, query: newQuery };
		},
		[filteredParams, router]
	);

	const onPriceSelectionDelete = useCallback(() => {
		// on facet change, should reset offset(page number)
		const { minPrice: _min, maxPrice: _max, limit, offset: _offset, ...newQuery } = filteredParams;
		const pathname = router.asPath.split('?').at(0);
		router.push({ pathname, query: newQuery }, undefined, {
			shallow: true,
		});
	}, [filteredParams, router]);

	const onFacetDelete = useCallback(
		(facetValue: string) => () => {
			// on facet change, should reset offset(page number)
			const { facet, limit, offset: _offset, ...newQuery } = filteredParams;
			const pathname = router.asPath.split('?').at(0);
			const index = selectedFacet.indexOf(facetValue);
			const newFacet = [...selectedFacet];
			index > -1 && newFacet.splice(index, 1);
			if (newFacet.length > 0) {
				Object.assign(newQuery, { facet: newFacet });
			}
			router.push({ pathname, query: newQuery }, undefined, {
				shallow: true,
			});
		},
		[filteredParams, router, selectedFacet]
	);

	const onDeleteAll = useCallback(() => {
		const { minPrice, maxPrice, facet, path, offset: _offset, ...newQuery } = query;
		const pathname = router.asPath.split('?').at(0);
		router.push({ pathname, query: newQuery }, undefined, {
			shallow: true,
		});
	}, [query, router]);

	const selectedSortOption = useMemo(
		() =>
			Array.isArray(filteredParams.orderBy)
				? filteredParams.orderBy.at(0)
				: filteredParams.orderBy ?? SORT_OPTIONS.defaultSortOptions[0].value,
		[filteredParams]
	);
	const sortOptions = useMemo(
		() =>
			String(data?.metaData?.price) === '1'
				? SORT_OPTIONS.defaultSortOptions.concat(SORT_OPTIONS.priceSortOptions)
				: SORT_OPTIONS.defaultSortOptions,
		[data?.metaData?.price]
	);

	const pageCount = useMemo(() => {
		if (total !== undefined) {
			return Math.ceil(total / filteredParams.limit);
		} else {
			return null;
		}
	}, [filteredParams.limit, total]);

	const pageNumber = useMemo(
		() => filteredParams.offset / filteredParams.limit + 1,
		[filteredParams.offset, filteredParams.limit]
	);

	const { nextPageLink, prevPageLink } = useMemo(() => ({
		prevPageLink: id !== EMPTY_STRING && pageNumber > 1 ? getHrefAsQueryString(getPageLink(getPageNumberForPaginationItem({ page: pageNumber, type: 'next', disabled: true }))) : null,
		nextPageLink: id !== EMPTY_STRING && pageCount && pageNumber < pageCount ? getHrefAsQueryString(getPageLink(getPageNumberForPaginationItem({ page: pageNumber, type: 'previous', disabled: true }))) : null
	}), [pageCount, pageNumber, id]);

	const onTriggerMarketingEvent = useCallback(
		(searchTerm: string) => {
			const data: Record<string,string> = {
				eMarketingSpotId: '',
				experimentResultTestElementId: '',
				baseMarketingSpotActivityID: '',
				baseMarketingSpotDataType: '',
				categoryId: '',
				activityId: '',
				personalizationID: '',
				experimentResultId: '',
				searchTerm,
				DM_ReqCmd: 'SearchDisplay',
				productId: '',
			};
			triggerMarketingEvent(true)(storeId, {}, data, params);
		},
		[storeId, params]
	);

	const clickActionGenerator = useCallback(
		(product: ProductType) => async () => {
			if (metaData?.espot) {
				const { espot, activity, experiment, testelement } = metaData as Required<typeof metaData>;
				const data = {
					evtype: 'CpgnClick',
					expDataType: 'CatalogEntryId',
					intv_id: activity,
					mpe_id: espot,
					experimentId: experiment,
					testElementId: testelement,
					expDataUniqueID: product.id,
				};
				await marketingClickInfoInvoker(true)({ storeId: settings.storeId, data, params });
			}
		},
		[metaData, params, settings]
	);

	useEffect(() => {
		if (pageCount) {
			if (pageNumber > pageCount) {
				onPageChange(pageCount);
			}
		}
	}, [onPageChange, pageCount, pageNumber]);

	useEffect(() => {
		setFacetEntries((facetEntries) => {
			const _facetEntries = union(facetEntries, facetEntryDataMap(data));
			return _facetEntries.length !== facetEntries.length ? _facetEntries : facetEntries;
		});
	}, [data]);

	
	const collectionPageData = useMemo(() => {
		if (!products) return null;
		let fullPathOfCategory = basePath;
		if(breadcrumb){
			const lastItem = breadcrumb[breadcrumb?.length - 1];
			fullPathOfCategory = lastItem?.href??"";
		}

		let tempCollectionPageDataFinal = defaultCollectionPageData;
			let productsData = [];  
			tempCollectionPageDataFinal = tempCollectionPageDataFinal
			.replace('replacePageCount', 36+'')   // TODO for last page, need to get actual count.
			.replaceAll('replacePLPURL', fullPathOfCategory)
			.replaceAll('replacePLPName',catGrpDetails?.at(0)?.name as string)
			.replaceAll('replacePLPDesc',pageNumber>1? 
				("Pagina "+pageNumber+" | "+catGrpDetails?.at(0)?.shortDescription as string) 
				: (catGrpDetails?.at(0)?.shortDescription as string) )
			for( let i=0; i<products.length; i++){
				let cardName = products?.at(i)?.name;
				let cardurl = basePath+products?.at(i)?.seo?.href;
				let cardPrice =  Array.isArray(products?.at(i)?.price)? products?.at(i)?.price?.find((p) => p.usage === 'Offer'): undefined; 
				const offerPrice = cardPrice?.value;
				// Perf issue . Mostly wont be used. Need to check later.
				//let cardStock = products?.at(i)?.stockAvailable?"https://schema.org/InStock":"https://schema.org/OutOfStock";
				productsData.push( 
					{"@type": "Offer","itemOffered": 
						{"@type": "Product","name": cardName,"url":cardurl,"@id":cardurl}
						,"price": offerPrice,"priceCurrency": "EUR"
					}); //,"availability": cardStock
			}
			tempCollectionPageDataFinal = tempCollectionPageDataFinal.replace("replaceCollectionItemList",JSON.stringify(productsData));
			return tempCollectionPageDataFinal;
	}, [
		categoryCanonicalPath, 
		pageCount,
		catGrpDetails?.at(0)?.name
	]);

	return {
		entitled: !!data?.contents || !isEmpty(facetFilters),
		products,
		clickActionGenerator,
		total,
		selectedSortOption,
		facetEntries,
		sortOptions,
		loading: !error && !data,
		error,
		selectedFacet,
		filteredParams,
		facetFilters,
		pageCount,
		pageNumber,
		onSortOptionChange,
		onPriceSelectionDelete,
		onFacetDelete,
		onDeleteAll,
		onPageChange,
		onTriggerMarketingEvent,
		categoryId,
		params,
		breadCrumbTrailEntryView: data?.breadCrumbTrailEntryView,
		getPageLink,
		categoryCanonicalPath,
		prevPageLink,
		nextPageLink,
		collectionPageData,
		catGrpDetails
	};
};
